
import { useContext, useEffect, useMemo, useState } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import { AuthContext } from "../../hoc/AuthContext";
import { BRANDS_ADD_PATH, getLoyaltyEditPath, getLoyaltyOffersEditPath, LOYALTY_ADD_PATH } from "./constants";
import { getProgramId } from "./utils";
import EventsOrderModal from "./components/EventsOrderModal";
import BrandsListing from "./components/BrandsListing";
import ViewImageModal from "./components/ViewImageModal";
import useLoyaltyOffers from "./hooks/useLoyaltyOffers";
import { getBrands } from "../../apiService/loyalty.service";

const normalizeBrandFromApi = (brand, index) => {
  const normalizedStatus =
    typeof brand?.status === "string"
      ? brand.status.toLowerCase()
      : brand?.status === 1 || brand?.status === true || brand?.is_active === true
        ? "active"
        : "inactive";

  return {
    ...brand,
    id: brand?.id ?? brand?._id ?? brand?.brand_id ?? `brand-${index}`,
    title_en:
      brand?.title_en ??
      brand?.brand_name_en ??
      brand?.brand_name ??
      brand?.name ??
      `Brand ${index + 1}`,
    brand_logo:
      brand?.brand_logo ??
      brand?.logo_url ??
      brand?.image_url ??
      brand?.image ??
      "",
    status: normalizedStatus,
    featured: Boolean(brand?.featured ?? brand?.is_featured ?? brand?.featured_brand ?? false),
  };
};

export default function LoyaltyOffers() {
  const navigate = useNavigate();
  const { selectedDomain } = useContext(AuthContext);
  const {
    brands,
    offers,
    coupons,
    selectedBrandId,
    selectedOfferId,
    togglingIds,
    handleSelectBrand,
    handleSelectOffer,
    handleToggleStatus,
    handleToggleFeature,
    fetchBrands,
    fetchOffersByBrand,
    fetchCouponsByOffer,
    updateOfferFeatured,
    saveCoupon,
    updateOffer,
  } = useLoyaltyOffers([]);

  const [eventsModal, setEventsModal] = useState({
    isOpen: false,
    programId: null,
    programTitle: "",
  });
  const [view, setView] = useState("brands");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [previewImage, setPreviewImage] = useState({
    isOpen: false,
    imageUrl: "",
    title: "",
  });

  useEffect(() => {
    let cancelled = false;

    const loadBrands = async () => {
      if (!selectedDomain?.id) {
        setCurrentPage(1);
        setTotalPages(1);
        fetchBrands([]);
        return;
      }

      try {
        const brandData = await getBrands(selectedDomain?.id, currentPage);
        if (cancelled) return;

        const brandList = Array.isArray(brandData)
          ? brandData
          : Array.isArray(brandData?.dataList)
            ? brandData.dataList
            : [];

        setCurrentPage(Number(brandData?.currentPage || currentPage));
        setTotalPages(Number(brandData?.totalPages || 1));

        const normalizedBrands = brandList.map((brand, index) => normalizeBrandFromApi(brand, index));

        fetchBrands(normalizedBrands.length ? normalizedBrands : []);
      } catch (error) {
        if (!cancelled) {
          setCurrentPage(1);
          setTotalPages(1);
          fetchBrands([]);
        }
      }
    };

    loadBrands();

    return () => {
      cancelled = true;
    };
  }, [selectedDomain?.id, currentPage]);

  useEffect(() => {
    if (view !== "offers" || !selectedBrandId) return;
    fetchOffersByBrand(selectedBrandId, []);
  }, [view, selectedBrandId, fetchOffersByBrand]);

  useEffect(() => {
    if (view !== "coupons" || !selectedOfferId) return;
    fetchCouponsByOffer(selectedOfferId, []);
  }, [view, selectedOfferId, fetchCouponsByOffer]);

  const selectedBrand = useMemo(
    () => brands.find((brand) => getProgramId(brand) === selectedBrandId) || null,
    [brands, selectedBrandId]
  );

  const selectedOffer = useMemo(
    () => offers.find((offer) => getProgramId(offer) === selectedOfferId) || null,
    [offers, selectedOfferId]
  );

  const openEventsModal = (brand) => {
    const brandId = getProgramId(brand);
    if (!brandId) return;

    setEventsModal({
      isOpen: true,
      programId: brandId,
      programTitle: brand.title_en || "",
    });
  };

  const handleBrandOffers = (brand) => {
    const brandId = getProgramId(brand);
    if (!brandId) return;

    const brandName =
      brand?.brand_name_en ||
      brand?.title_en ||
      brand?.brand_name ||
      brand?.name ||
      "Brand";

    navigate(`/services/loyalty-offers/${brandId}/offers`, {
      state: { brandName },
    });
  };

  const handleOfferClick = (offer) => {
    const offerId = getProgramId(offer);
    if (!offerId) return;
    const brandId = selectedBrandId;
    if (!brandId) return;
    navigate(`/services/loyalty-offers/${brandId}/offers/${offerId}/coupons`);
  };

  const handleBackToBrands = () => setView("brands");
  const handleBackToOffers = () => setView("offers");

  const handlePreviewImage = (imageUrl, title = "Image preview") => {
    setPreviewImage({
      isOpen: Boolean(imageUrl),
      imageUrl: imageUrl || "",
      title,
    });
  };

  const handlePageChange = (nextPage) => {
    const safePage = Math.min(Math.max(nextPage, 1), totalPages || 1);
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
    }
  };

  const saveSampleCoupon = () => {
    const nextCoupon = {
      id: Date.now(),
      code: `NEW-${Math.floor(Math.random() * 900 + 100)}`,
      discount: "Custom Offer",
      is_active: true,
    };

    saveCoupon(nextCoupon);
  };

  const updateSampleOffer = () => {
    if (!selectedOffer) return;
    updateOffer({ ...selectedOffer, title_en: `${selectedOffer.title_en || "Offer"} Updated` });
  };

  return (
    <Layout>
      <main>
        <EventsOrderModal
          isOpen={eventsModal.isOpen}
          programId={eventsModal.programId}
          programTitle={eventsModal.programTitle}
          onClose={() =>
            setEventsModal({
              isOpen: false,
            programId: null,
              programTitle: "",
            })
          }
        />

        <ViewImageModal
          isOpen={previewImage.isOpen}
          imageUrl={previewImage.imageUrl}
          title={previewImage.title}
          onClose={() => handlePreviewImage("", "")}
        />

        <div className="mx-auto max-w-7xl my-2 px-4 lg:px-0">
          <div className="bg-white rounded-2xl shadow-lg">
            <div className="mx-auto p-6 bg-slate-50 rounded-t-2xl">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-gray-900">Loyalty Offers(Brand)</h1>
                  <p className="mt-1 text-sm text-gray-600">
                    Manage loyalty Offers settings, Brands and coupons.
                  </p>
                </div>

                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-600"
                  onClick={() => navigate(BRANDS_ADD_PATH)}
                >
                  <PlusIcon className="h-4 w-4" />
                  Add
                </button>
              </div>
            </div>

            {view === "brands" ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50 text-left text-sm font-semibold text-gray-500">
                    <tr>
                      <th className="px-6 py-4">Sr No.</th>
                      <th className="px-6 py-4">Brand Logo</th>
                      <th className="px-6 py-4">Brand Name</th>
                      <th className="px-6 py-4">Status</th>
                      {/* <th className="px-6 py-4">Featured</th> */}
                      <th className="px-6 py-4">Action</th>
                    </tr>
                  </thead>

                  <BrandsListing
                    brands={brands}
                    isSavingOrder={false}
                    togglingIds={togglingIds}
                    onToggleStatus={handleToggleStatus}
                    onPreviewImage={handlePreviewImage}
                    // onToggleFeature={handleToggleFeature}
                    onEdit={(brandId) => navigate(getLoyaltyOffersEditPath(brandId))}
                    onOpenOffers={handleBrandOffers}
                  />
                </table>

                <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3">
                  <button
                    type="button"
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                  >
                    Previous
                  </button>

                  <span className="text-sm font-medium text-gray-700">
                    Page {currentPage} of {totalPages || 1}
                  </span>

                  <button
                    type="button"
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages || totalPages <= 1}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}

            {view === "offers" && selectedBrand ? (
              <div className="p-6">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700">Brand</p>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedBrand.title_en}</h2>
                  </div>
                  <button
                    type="button"
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
                    onClick={handleBackToBrands}
                  >
                    Back to Brands
                  </button>
                </div>

                <div className="space-y-3">
                  {offers.length ? (
                    offers.map((offer) => (
                      <div
                        key={getProgramId(offer) || `${selectedBrandId}-offer`}
                        className="flex items-center justify-between rounded-xl border border-gray-200 bg-slate-50 p-4"
                      >
                        <button
                          type="button"
                          className="text-left text-base font-semibold text-gray-800"
                          onClick={() => handleOfferClick(offer)}
                        >
                          {offer.title_en || "Untitled offer"}
                        </button>
                        <button
                          type="button"
                          className="text-sm font-semibold text-cyan-600"
                          onClick={() => handleOfferClick(offer)}
                        >
                          View Coupons
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No offers found for this brand.</p>
                  )}
                </div>
              </div>
            ) : null}

            {view === "coupons" && selectedOffer ? (
              <div className="p-6">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-cyan-700">Offer</p>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedOffer.title_en}</h2>
                  </div>
                  <button
                    type="button"
                    className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700"
                    onClick={handleBackToOffers}
                  >
                    Back to Offers
                  </button>
                </div>

                <div className="space-y-3">
                  {coupons.length ? (
                    coupons.map((coupon) => (
                      <div
                        key={coupon.id || coupon.code}
                        className="flex items-center justify-between rounded-xl border border-gray-200 bg-slate-50 p-4"
                      >
                        <div>
                          <p className="font-semibold text-gray-800">{coupon.code || "Coupon code"}</p>
                          <p className="text-sm text-gray-600">{coupon.discount || "Discount"}</p>
                        </div>
                        <span
                          className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                            coupon.is_active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {coupon.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No coupons found for this offer.</p>
                  )}
                </div>

                <div className="mt-5 flex justify-end">
                  <button
                    type="button"
                    className="rounded-lg bg-cyan-500 px-4 py-2 text-sm font-semibold text-white"
                    onClick={saveSampleCoupon}
                  >
                    Save Coupon
                  </button>
                </div>
              </div>
            ) : null}

          </div>
        </div>
      </main>
    </Layout>
  );
}

